import React, { useEffect, useLayoutEffect, useState } from 'react';
import VerifyEmailPage from './features/auth/VerifyEmailPage';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { cmsApi } from '@/services/api';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { CartProvider } from '@/context/CartContext';
import { NavCategoriesProvider } from '@/context/NavCategoriesContext';
import { Header } from '@/components/shared/Header';
import { Footer } from '@/components/shared/Footer';
import { CookieBanner } from '@/components/shared/CookieBanner';
import { HomePage } from '@/features/home/HomePage';
import { ProductsPage } from '@/features/products/ProductsPage';
import { ProductDetailPage } from '@/features/products/ProductDetailPage';
import { StorePage } from '@/features/stores/StorePage';
import { StoresPage } from '@/features/stores/StoresPage';
import { MapPage } from '@/features/stores/MapPage';
import { CartPage } from '@/features/cart/CartPage';
import { CheckoutPage } from '@/features/cart/CheckoutPage';
import { CheckoutSuccessPage } from '@/features/cart/CheckoutSuccessPage';
import { LoginPage, ForgotPasswordPage, ResetPasswordPage } from '@/features/auth';
import { BliPartnerPage, AnslutByggvaruhusPage, RegisterRedirect } from '@/features/partner';
import { AdminPage } from '@/features/admin/AdminPage';
import { StoreDashboardPage } from '@/features/store/StoreDashboardPage';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { AboutPage, TermsPage, PrivacyPage, FAQPage, ContactPage, AnvandarvillkorRegistrationPage } from '@/features/static';
import InvoiceDataPage from '@/features/admin/InvoiceDataPage';
import MaintenancePage from '@/features/home/MaintenancePage';
import './App.css';
import { SeoNoIndex } from '@/components/seo/SeoMeta';
import { scheduleReleaseUiScrollLock } from '@/utils/releaseUiScrollLock';
import { canUseStatistics } from '@/utils/cookieConsent';
import { clearBehaviorTrackingData } from '@/utils/behaviorTracking';
import { loadMetaPixelWhenConsented } from '@/utils/metaPixel';

/** Efter inlogg/utlogg kan Radix lämna overlay/body-lås; rensa synkront innan målning. */
const ScrollLockSweepAfterAuth = () => {
  const { user } = useAuth();
  const key = user ? `${user.id ?? ''}:${user.email ?? ''}` : '';
  useLayoutEffect(() => {
    scheduleReleaseUiScrollLock();
  }, [key]);
  return null;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    scheduleReleaseUiScrollLock();
  }, [pathname]);
  return null;
};

const Layout = ({ children }) => (
  <div className="flex flex-col min-h-screen">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
    <CookieBanner />
  </div>
);

const AppContent = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [maintenanceTitle, setMaintenanceTitle] = useState('');
  const [maintenanceEta, setMaintenanceEta] = useState('');
  const [checkingMaintenance, setCheckingMaintenance] = useState(true);

  useEffect(() => {
    if (!canUseStatistics()) {
      clearBehaviorTrackingData();
    }
    loadMetaPixelWhenConsented();
  }, []);

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const res = await cmsApi.getMaintenanceStatus();
        setMaintenanceMode(res.data.maintenance_mode);
        setMaintenanceMessage(res.data.maintenance_message || '');
        setMaintenanceTitle(res.data.maintenance_title || '');
        setMaintenanceEta(res.data.maintenance_eta || '');
      } catch (error) {
        console.error('Error checking maintenance status:', error);
      } finally {
        setCheckingMaintenance(false);
      }
    };
    checkMaintenance();
  }, []);

  const allowedPaths = ['/logga-in', '/admin', '/glomt-losenord', '/aterstall-losenord'];
  const isAllowedPath = allowedPaths.some(path => location.pathname.startsWith(path));

  if (!checkingMaintenance && maintenanceMode && !user?.is_admin && !isAllowedPath) {
    return (
      <MaintenancePage
        title={maintenanceTitle}
        message={maintenanceMessage}
        eta={maintenanceEta}
      />
    );
  }

  return (
    <>
      <ScrollToTop />
      {maintenanceMode && user?.is_admin && (
        <div className="bg-yellow-500 text-yellow-900 px-4 py-2 text-center text-sm font-medium">
          Underhållsläge aktivt - Endast du som admin kan se sidan
        </div>
      )}
      <Routes>
        {/* Main Pages */}
        <Route path="/" element={<Layout><HomePage /></Layout>} />
        <Route path="/produkter" element={<Layout><ProductsPage /></Layout>} />
        <Route path="/produkt/:id" element={<Layout><ProductDetailPage /></Layout>} />
        <Route path="/butik/:id" element={<Layout><StorePage /></Layout>} />
        <Route path="/butiker" element={<Layout><StoresPage /></Layout>} />
        <Route path="/karta" element={<Layout><MapPage /></Layout>} />

        {/* Cart & Checkout */}
        <Route path="/varukorg" element={<Layout><CartPage /></Layout>} />
        <Route path="/kassa" element={<Layout><CheckoutPage /></Layout>} />
        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />

        {/* Auth */}
        <Route path="/logga-in" element={<LoginPage />} />
        <Route path="/registrera" element={<RegisterRedirect />} />
        <Route path="/glomt-losenord" element={<ForgotPasswordPage />} />
        <Route path="/verifiera-email/:token" element={<VerifyEmailPage />} />
        <Route path="/aterstall-losenord" element={<ResetPasswordPage />} />

        {/* Admin */}
        <Route path="/admin" element={<Layout><AdminPage /></Layout>} />
        <Route path="/admin/fakturaunderlag" element={<Layout><InvoiceDataPage /></Layout>} />

        {/* Store Dashboard */}
        <Route path="/butik-dashboard" element={<Layout><StoreDashboardPage /></Layout>} />
        <Route path="/min-butik" element={<Layout><StoreDashboardPage /></Layout>} />
        <Route path="/butik-dashboard/subscription/success" element={<Layout><StoreDashboardPage /></Layout>} />

        {/* Customer Profile */}
        <Route path="/min-profil" element={<Layout><ProfilePage /></Layout>} />

        {/* Static Pages */}
        <Route path="/om-oss" element={<Layout><AboutPage /></Layout>} />
        <Route path="/kopvillkor" element={<Layout><TermsPage /></Layout>} />
        <Route path="/integritetspolicy" element={<Layout><PrivacyPage /></Layout>} />
        <Route path="/anvandarvillkor-kund" element={<Layout><AnvandarvillkorRegistrationPage variant="consumer" /></Layout>} />
        <Route path="/anvandarvillkor-butik" element={<Layout><AnvandarvillkorRegistrationPage variant="store" /></Layout>} />
        <Route path="/faq" element={<Layout><FAQPage /></Layout>} />
        <Route path="/kontakt" element={<Layout><ContactPage /></Layout>} />
        <Route path="/cookies" element={<Layout><PrivacyPage /></Layout>} />
        <Route path="/bli-partner" element={<Layout><BliPartnerPage /></Layout>} />
        <Route path="/bli-partner/anslut-byggvaruhus" element={<Layout><AnslutByggvaruhusPage /></Layout>} />
      </Routes>
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <SeoNoIndex />
        <AuthProvider>
          <ScrollLockSweepAfterAuth />
          <CartProvider>
            <NavCategoriesProvider>
              <AppContent />
            </NavCategoriesProvider>
            <Toaster 
              position="top-right" 
              richColors 
              toastOptions={{
                style: { fontFamily: 'Public Sans, sans-serif' }
              }}
            />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
